import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthServices from "../services/auth/useAuthService";
import { User, Location, SupportedLanguage } from "../types/Auth";
import i18n from "../i18n/i18n";

interface AuthContextType {
    userInfo: User | null;
    isAuthenticated: boolean;
    doSignIn: (email: string, password: string) => Promise<void>;
    signInError: string | null;
    removeSignInError: () => void;
    doSignOut: () => Promise<void>;
    activeLocationId: number | null;
    activeLocation: Location | null;
    changeActiveLocation: (locationId: number | null) => void;
    updateLocationDetails: (locationId: number, name: string, address: string, numstations: number) => void;
    setLanguage: (language: SupportedLanguage) => Promise<void>;
    language: SupportedLanguage;
    locations: Location[];
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface LocationState {
    from?: { pathname: string };
}
interface AuthProviderProps {
    children: ReactNode;
}

export enum eResponseCodes {
    SUCCESS = 'success',
    INVALID_SOURCE_IDENTIFIER = 'invalidSourceIdentifier',
    invalidSession = 'invalidSession',
    invalidMessage = 'invalidMessage',
    invalidData = 'invalidData',
    INVALID_LOGIN = 'invalidLogin',
    insufficientRights = 'insufficientRights',
    unknown_error = 'unknown_error',
    handlerNotInitialized = 'handlerNotInitialized'
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [userInfo, setUserInfo] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [signInError, setSignInError] = useState<string | null>(null);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [activeLocationId, setActiveLocationId] = useState<number | null>(null);
    const [activeLocation, setActiveLocation] = useState<Location | null>(null);

    const location = useLocation() as unknown as { state?: LocationState };
    const navigate = useNavigate();
    const { signIn, getSession, signOut, updateLanguage } = useAuthServices();

    const removeSignInError = () => {
        setSignInError(null);
    }

    const changeActiveLocation = (locationId: number | null) => {
        setActiveLocationId(locationId);
        sessionStorage.setItem('activeLocationId', locationId ? locationId.toString() : '');
        localStorage.setItem('activeLocationId', locationId ? locationId.toString() : '');

        window.location.reload();
    }

    const updateLocationDetails = (locationId: number, name: string, address: string, numstations: number) => {
        if (userInfo && userInfo.locations) {
            const updatedLocations = userInfo.locations.map(loc =>
                loc.id === locationId ? { ...loc, name, address, numstations } : loc
            );
            setUserInfo({ ...userInfo, locations: updatedLocations });
        }
    }

    const setLanguage = async (language: SupportedLanguage) => {
        try {
            const res = await updateLanguage(language);
            if (res.result === eResponseCodes.SUCCESS) {
                if (userInfo) {
                    setUserInfo({ ...userInfo, language });
                }
                i18n.changeLanguage(language);
                localStorage.setItem('locale', language);
            } else {
                console.error("Error updating language:", res.error || res.data);
            }
        } catch (error) {
            console.error("Error updating language:", error);
        }
    };

    const getActiveLocation = (): number | null => {
        const sessionLocationId = sessionStorage.getItem('activeLocationId');
        if (sessionLocationId) {
            return +sessionLocationId;
        }
        const localLocationId = localStorage.getItem('activeLocationId');
        if (localLocationId) {
            return +localLocationId;
        }

        return null;
    }

    const hasAccessToLocation = (locationId: number, locations: Location[]): boolean => {
        return locations.some(loc => loc.id === locationId);
    }

    const doSignIn = async (email: string, password: string): Promise<void> => {
        try {
            const res = await signIn(email, password);
            if (res.result === eResponseCodes.SUCCESS) {
                const actLocation = getActiveLocation();
                if (!actLocation || !hasAccessToLocation(actLocation, res.data.locations)) {
                    changeActiveLocation(res.data.locations[0].id);
                } else {
                    setActiveLocationId(actLocation);
                }
                setUserInfo(res.data);
                setIsAuthenticated(true);
                if (res.data.language) {
                    i18n.changeLanguage(res.data.language);
                    localStorage.setItem('locale', res.data.language);
                }

                // check if we have a query param for redirect
                const redirectPath = location.state?.from?.pathname || "/dashboard";
                navigate(redirectPath ?? '/dashboard', { replace: true });
            } else if (res.result === eResponseCodes.INVALID_LOGIN) {
                setSignInError("login.invalidLogin");
                setIsAuthenticated(false);
            } else {
                console.error("Error during sign in:", res.error);
                setSignInError("login.unknownError");
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error("Error during sign in:", error);
            setSignInError("login.unknownError");
            setIsAuthenticated(false);
        }
    };

    const doSignOut = async () => {
        try {
            await signOut();
            setUserInfo(null);
            setIsAuthenticated(false);
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const initialize = useCallback(async (): Promise<void> => {
        try {
            const res = await getSession();
            if (res.result === eResponseCodes.SUCCESS) {
                const actLocation = getActiveLocation();
                if (!actLocation || !hasAccessToLocation(actLocation, res.data.locations)) {
                    changeActiveLocation(res.data.locations[0].id);
                    setActiveLocationId(res.data.locations[0].id);
                } else {
                    setActiveLocationId(actLocation);
                }
                setUserInfo(res.data);
                setIsAuthenticated(true);
                if (res.data.language) {
                    i18n.changeLanguage(res.data.language);
                    localStorage.setItem('locale', res.data.language);
                }
            } else {
                setUserInfo(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            setUserInfo(null);
            setIsAuthenticated(false);
        } finally {
            setIsReady(true);
        }
    }, [getSession]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    useEffect(() => {
        if (activeLocationId && userInfo?.locations) {
            const location = userInfo.locations.find(loc => loc.id === activeLocationId);
            setActiveLocation(location || null);
        } else {
            setActiveLocation(null);
        }
    }, [activeLocationId, userInfo]);

    if (!isReady) {
        return null; // or a loading spinner
    }

    return (
        <AuthContext.Provider value={{
            userInfo,
            isAuthenticated,
            doSignIn,
            signInError,
            removeSignInError,
            doSignOut,
            activeLocationId,
            activeLocation,
            changeActiveLocation,
            updateLocationDetails,
            setLanguage,
            language: userInfo?.language ?? 'en',
            locations: userInfo?.locations ?? [],
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
